import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { SHARED_LINK_QUERY, FILE_MEASUREMENTS_QUERY, SHARED_FILE_MEASUREMENTS_QUERY } from '../../graphql/queries';
import {
  CREATE_2D_MEASUREMENT_MUTATION,
  RENAME_MEASUREMENT_MUTATION,
  DELETE_MEASUREMENT_MUTATION,
} from '../../graphql/mutations';
import {
  MeasureKind, MeasureTool, MeasureVM, Pt, SnapIndex, SvgScale, Unit,
  createSnapIndexBuilder, detectSvgScale, formatArea, formatLength,
  measureValues, orthoConstrain, parseViewBox, snapAt,
} from './dwgMeasure';
import { DWGMeasureOverlay } from './DWGMeasureOverlay';
import {
  IdHit, IdIndex, IdPointKind, IdPointsFile, IdWorld,
  bboxFrame, buildIdIndex, idPointClipboard, idPointText, formatIdCoord, nearestIdPoint, worldAt,
} from './dwgIdPoints';
import { canUnpackGzip, cdnAssetUrl, cdnFolder, jsonOf, textOf } from '../../utils/cdnAssets';
import styles from './DWGViewer.module.css';

const TOKEN_KEY = 'geodraft_token';

const FILE_QUERY = gql`
  query DWGFile($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
    }
  }
`;

type Stage = 'checking' | 'processing' | 'loading' | 'ready' | 'failed' | 'notprepared';

type DwgTab = { key: string; name: string; file: string; points?: string };

type IdPin = { id: number; doc: Pt; world: IdWorld; kind: IdPointKind | 'cursor' };

const ID_PIN_LIMIT = 30;

const ID_KIND_LABEL: Record<IdPin['kind'], string> = {
  survey: 'Survey point', block: 'Block insert', vertex: 'Vertex', cursor: 'Cursor (no Z)',
};

const TOOLS: { tool: MeasureTool; icon: string; label: string; key: string }[] = [
  { tool: 'pan',      icon: 'pan_tool',   label: 'Pan / select',        key: 'Esc' },
  { tool: 'line',     icon: 'straighten', label: 'Measure distance',    key: 'L' },
  { tool: 'polyline', icon: 'polyline',   label: 'Measure path length', key: 'P' },
  { tool: 'rect',     icon: 'rectangle',  label: 'Measure rectangle',   key: 'R' },
  { tool: 'area',     icon: 'pentagon',   label: 'Measure area',        key: 'A' },
  { tool: 'id',       icon: 'pin_drop',   label: 'Identify point (X, Y, Z)', key: 'I' },
];

const isMeasureKind = (t: MeasureTool): t is MeasureKind => t !== 'pan' && t !== 'id';

const KIND_ICON: Record<MeasureKind, string> = {
  line: 'straighten', polyline: 'polyline', rect: 'rectangle', area: 'pentagon',
};

const KIND_LABEL: Record<MeasureKind, string> = {
  line: 'Line', polyline: 'Path', rect: 'Rectangle', area: 'Area',
};

const UNITS: Unit[] = ['u', 'mm', 'cm', 'm', 'km', 'in', 'ft'];
const MEASURE_KINDS: MeasureKind[] = ['line', 'polyline', 'rect', 'area'];
const SNAP_TOL_PX = 12;
// On-screen width (CSS px) every stroke is held to, at any zoom — see applyView.
const COSMETIC_STROKE_PX = 1.3;

const ID_HINTS = {
  ready: 'Click to read X, Y, Z — survey points snap automatically, Esc exits',
  loading: 'Loading survey points…',
  none: 'X / Y only — re-convert this drawing to read survey Z values',
  outside: 'That spot is outside the sheet’s drawing viewports',
  legacySheet: 'Re-convert this drawing to identify points on its sheets',
};

const HINTS: Record<MeasureKind, string> = {
  line: 'Click two points — Shift locks to horizontal / vertical, Esc cancels',
  polyline: 'Click points along the path — double-click or Enter to finish, Esc cancels',
  rect: 'Click two opposite corners — Esc cancels',
  area: 'Click the outline points — click the first point or press Enter to close, Esc cancels',
};

// Self-hosted 2D viewer — THE viewer for DWG/DXF (no Autodesk).
// Pipeline: Batch job renders DWG/DXF → SVG via LibreDWG + ezdxf; conversion is
// normally triggered at upload time, so opening here just polls S3-backed status
// and inlines the SVG for true vector pan/zoom. Mounted on two routes:
// /projects/:projectId/viewer-2d/:fileId (authenticated) and
// /share/:token/viewer-2d/:fileId (public share link, no account).
// Camera pose stream from the Combined View's point-cloud pane (world-frame
// metres). Provided only when embedded next to a Smart View — enables the
// live-position dot on georeferenced drawings.
export type CameraPoseSource = {
  getLatest: () => { x: number; y: number; z: number; heading: number } | null;
  subscribe: (fn: (p: { x: number; y: number; z: number; heading: number }) => void) => () => void;
};

// Metres per drawing unit by DXF $INSUNITS code (factor mirror of
// INSUNITS_TO_UNIT in dwgMeasure.ts). A drawing declaring any OTHER code
// (notably 0 = unitless, the DXF default) does NOT get a guessed unit —
// guessing metres would place a confidently wrong in-bounds dot; the feature
// reports itself unavailable instead.
const INSUNITS_METERS: Record<string, number> = {
  '1': 0.0254, '2': 0.3048, '4': 0.001, '5': 0.01, '6': 1, '7': 1000,
};

type DWGViewerProps = {
  fileId?: string;
  embedded?: boolean;
  poseSource?: CameraPoseSource;
};

const DWGViewer = ({ fileId: fileIdProp, embedded = false, poseSource }: DWGViewerProps) => {
  const { fileId: fileIdParam, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const fileId = fileIdProp ?? fileIdParam;
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('checking');
  const [error, setError] = useState('');
  // Object wrapper (not the raw string): two tabs can render byte-identical
  // SVGs, and an identical-string setState would bail out and skip re-injection.
  const [svg, setSvg] = useState<{ text: string } | null>(null);
  // The SVG is rendered dark (#06080b background — old renders' #101418 is
  // normalised at inject time; light strokes for ACI colour 7).
  // White mode inverts it client-side: invert(1) swaps whites/blacks, and
  // hue-rotate(180deg) rotates the inverted hues back so red lines stay red.
  // Works on every already-rendered drawing — no re-conversion needed.
  const [lightBg, setLightBg] = useState(true);
  // New conversions bake a LIGHT paper background (data-bg-mode="light") with
  // exact CAD colors — shown filter-free in light mode, inverted for dark.
  // Legacy renders baked dark and invert the other way around.
  const [lightBaked, setLightBaked] = useState(false);
  // Bumped by the Retry button — re-runs the trigger+poll effect from scratch.
  const [retryTick, setRetryTick] = useState(0);
  const svgHostRef = useRef<HTMLDivElement>(null);

  // Pan/zoom state lives in a ref (mutated per event, applied directly to the
  // DOM) — React re-renders per wheel tick would be far too slow on big SVGs.
  const viewRef = useRef({ x: 0, y: 0, scale: 1 });
  const wrapRef = useRef<HTMLDivElement>(null);

  const [tool, setTool] = useState<MeasureTool>('pan');
  const [snapOn, setSnapOn] = useState(true);
  const [unit, setUnit] = useState<Unit>('m');
  const [measurements, setMeasurements] = useState<MeasureVM[]>([]);
  const [panelOpen, setPanelOpen] = useState(true);
  const [snapProgress, setSnapProgress] = useState(1);
  const [snapTruncated, setSnapTruncated] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  // Model + paper layouts as tabs (tabs.json manifest); null = legacy
  // single-SVG conversion.
  const [tabs, setTabs] = useState<DwgTab[] | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  // ID points: pins are per tab and not saved; the snap targets (survey points
  // with Z) come from the converter's per-tab sidecar, loaded on first use.
  const [idPins, setIdPins] = useState<IdPin[]>([]);
  const [idState, setIdState] = useState<'loading' | 'ready' | 'none'>('none');
  const [idOutside, setIdOutside] = useState(false);
  const idIndexRef = useRef(new Map<number, IdIndex | null>());
  const idLoadingRef = useRef(new Set<number>());
  const idSeqRef = useRef(0);
  const activeTabRef = useRef(0);
  // Un-gated copy of data-model-bbox: ID points need no metre conversion, so
  // a unitless drawing still gets X/Y from it (the geo dot's copy is nulled).
  const idBboxRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  const addIdPinRef = useRef<(doc: Pt, hit: IdHit | null) => void>(() => {});

  const overlayRef = useRef<SVGSVGElement>(null);
  const controllerRef = useRef<DWGMeasureOverlay | null>(null);
  const snapIndexRef = useRef<SnapIndex | null>(null);
  const scaleRef = useRef<SvgScale>({ unitsPerSvg: 1, defaultUnit: 'u' });
  const vbRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const toolRef = useRef<MeasureTool>('pan');
  const snapOnRef = useRef(true);
  const unitRef = useRef<Unit>('m');
  const measurementsRef = useRef<MeasureVM[]>([]);
  const lastMouseRef = useRef<{ x: number; y: number; shift: boolean } | null>(null);
  // Embedded in the combined view: global key shortcuts must not fire while the
  // pointer is over a sibling pane.
  const hoveredRef = useRef(false);
  // Server ids deleted locally: filtered out of query re-emits until the cache
  // catches up. Tmp ids deleted while their create was still in flight: the
  // create's .then must issue the delete the click could not.
  const deletedIdsRef = useRef(new Set<string>());
  const deletedTmpRef = useRef(new Set<string>());

  const lightBgRef = useRef(true);
  const tabsRef = useRef<DwgTab[] | null>(null);
  const activeTabKeyRef = useRef<string | null>(null);

  // ── Live position from the point-cloud pane (Combined View only) ───────────
  // World metres → drawing units → SVG coords (data-model-bbox = the exact
  // model-space box the converter fit onto the viewBox, y flipped) → screen px.
  const [geoTrackOn, setGeoTrackOn] = useState(false);
  const [geoStatus, setGeoStatus] = useState<'ok' | 'no-bbox' | 'no-units'>('no-bbox');
  const [geoOut, setGeoOut] = useState(false);
  // Tracking on but no pose received yet (cloud still loading, or the legacy
  // Potree engine which never emits poses) — honest feedback beats a dead dot.
  const [geoWaiting, setGeoWaiting] = useState(false);
  const geoTrackOnRef = useRef(false);
  const geoOutRef = useRef(false);
  const geoWaitingRef = useRef(false);
  const modelBboxRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  const metersToDrawingRef = useRef(1);
  const lastPoseRef = useRef<{ x: number; y: number; z: number; heading: number } | null>(null);
  const geoDotRef = useRef<HTMLDivElement>(null);
  const geoWedgeRef = useRef<HTMLDivElement>(null);
  // Plain closure ref (not useCallback) — reads a dozen refs and is called from
  // applyView / pose ticks / toggle changes without dependency ordering issues.
  const updateGeoDotRef = useRef<() => void>(() => {});
  // Which tab the svg ABOUT to be injected belongs to — read by the inject
  // effect for the per-tab unit key.
  const pendingTabKeyRef = useRef<string | null>(null);
  const tabCacheRef = useRef(new Map<number, string>());
  // Latest-wins token: a slow tab fetch resolving after a newer switch (or
  // after a reload) must not apply its stale result.
  const tabReqRef = useRef(0);
  // Cache-busting generation from tabs.json — a force re-render mints new tab
  // URLs so the browser can never mix tab SVGs from different renders.
  const tabGenRef = useRef(0);

  toolRef.current = tool;
  snapOnRef.current = snapOn;
  unitRef.current = unit;
  measurementsRef.current = measurements;
  lightBgRef.current = lightBg;
  tabsRef.current = tabs;
  activeTabKeyRef.current = tabs ? tabs[activeTab]?.key ?? null : null;
  activeTabRef.current = activeTab;

  // Null-layout rows (made before tabs existed, or on a legacy render) belong
  // to no tab — surface them on the FIRST tab so they stay visible and
  // deletable rather than silently unreachable.
  const visibleOf = (list: MeasureVM[]): MeasureVM[] => {
    const t = tabsRef.current;
    if (!t) return list;
    const key = activeTabKeyRef.current;
    const includeNull = key !== null && t[0]?.key === key;
    return list.filter((m) => m.layout === key || (m.layout === null && includeNull));
  };
  const visibleMeasurements = visibleOf(measurements);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY) || '';
    return { Authorization: `Bearer ${token}` };
  }, []);

  // The SVG tabs and ID points come from CloudFront where prod hands out a
  // signed folder; the manifest always comes from the API, so a re-render is
  // seen at once. Null = the API routes.
  const assetFetch = useCallback(async (name: string, apiPath: string) => {
    const folder = fileId && canUnpackGzip()
      ? await cdnFolder(fileId, isShare ? { shareToken } : { headers: authHeader() })
      : null;
    if (folder) return fetch(cdnAssetUrl(folder, name));
    return fetch(apiPath, isShare ? undefined : { headers: authHeader() });
  }, [fileId, isShare, shareToken, authHeader]);

  const { data } = useQuery(FILE_QUERY, { variables: { id: fileId }, skip: !fileId || isShare });
  // Share visitors have no account — file info comes from the public share-link query.
  const { data: shareData } = useQuery(SHARED_LINK_QUERY, {
    variables: { token: shareToken },
    skip: !isShare,
  });
  const fileInfo = isShare
    ? shareData?.sharedLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;

  const { data: measurementData } = useQuery(FILE_MEASUREMENTS_QUERY, {
    variables: { fileId },
    skip: !fileId || isShare,
    fetchPolicy: 'cache-and-network',
  });
  // Share links show the saved measurements read-only; a visitor's own stay in this page.
  const { data: sharedMeasurementData } = useQuery(SHARED_FILE_MEASUREMENTS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !fileId || !isShare,
    fetchPolicy: 'cache-and-network',
  });

  const [create2dMeasurement] = useMutation(CREATE_2D_MEASUREMENT_MUTATION);
  const [renameMeasurement] = useMutation(RENAME_MEASUREMENT_MUTATION);
  const [deleteMeasurement] = useMutation(DELETE_MEASUREMENT_MUTATION);

  useEffect(() => {
    const rows = isShare ? sharedMeasurementData?.sharedFileMeasurements : measurementData?.fileMeasurements;
    if (!rows) return;
    const vms: MeasureVM[] = rows
      .filter((r: { kind: string }) => (MEASURE_KINDS as string[]).includes(r.kind))
      .map((r: { id: string; kind: MeasureKind; name?: string | null; layout?: string | null; points: { x: number; y: number }[]; distance?: number | null; area?: number | null }) => ({
        id: r.id,
        kind: r.kind,
        name: r.name || '',
        layout: r.layout ?? null,
        pts: (r.points || []).map((p) => ({ x: p.x, y: p.y })),
        distance: r.distance ?? null,
        area: r.area ?? null,
        readOnly: isShare,
      }));
    setMeasurements((prev) => {
      // Union by id: the cached list lags behind creates/deletes made this
      // session, so rows the server list is missing are kept and rows deleted
      // locally are dropped.
      const serverIds = new Set(vms.map((v) => v.id));
      const extras = prev.filter((m) => !serverIds.has(m.id));
      return [...vms.filter((v) => !deletedIdsRef.current.has(v.id)), ...extras];
    });
  }, [measurementData, sharedMeasurementData, isShare]);

  // ── Trigger + poll until the SVG exists ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const shareQS = isShare ? `?shareToken=${encodeURIComponent(shareToken!)}` : '';

    const loadSvg = async () => {
      setStage('loading');
      tabReqRef.current++;
      const opts = isShare ? undefined : { headers: authHeader() };
      // Tab-aware conversions carry a manifest; legacy ones only drawing.svg.
      const mRes = await fetch(`/api/dwg2d/manifest/${fileId}${shareQS}`, opts);
      if (mRes.ok) {
        const manifest = await mRes.json() as { tabs?: DwgTab[]; generated?: number };
        if (manifest.tabs?.length) {
          tabGenRef.current = manifest.generated ?? 0;
          const g = shareQS ? `${shareQS}&g=${tabGenRef.current}` : `?g=${tabGenRef.current}`;
          const tRes = await assetFetch('tab-0.svg.gz', `/api/dwg2d/tab/${fileId}/0${g}`);
          // A manifest without its tab files is a half-uploaded render — fail
          // loudly (retry UI) rather than fall back to a tabless view that
          // would draw every layout's measurements on one SVG.
          if (!tRes.ok) throw new Error('Could not download the drawing tab');
          const text = await textOf(tRes);
          if (cancelled) return;
          tabCacheRef.current.clear();
          tabCacheRef.current.set(0, text);
          idIndexRef.current.clear();
          setTabs(manifest.tabs);
          setActiveTab(0);
          pendingTabKeyRef.current = manifest.tabs[0].key;
          setSvg({ text });
          setStage('ready');
          return;
        }
      }
      const res = await assetFetch('drawing.svg.gz', `/api/dwg2d/file/${fileId}${shareQS}`);
      if (!res.ok) throw new Error('Could not download the rendered drawing');
      const text = await textOf(res);
      if (cancelled) return;
      setTabs(null);
      pendingTabKeyRef.current = null;
      setSvg({ text });
      setStage('ready');
    };

    const poll = async (first: boolean) => {
      try {
        const res = await fetch(`/api/dwg2d/status/${fileId}${shareQS}`, isShare ? undefined : { headers: authHeader() });
        const st = await res.json() as { status: string; error?: string; active?: boolean };
        if (cancelled) return;
        if (st.status === 'ready') { await loadSvg(); return; }
        if (st.status === 'failed' && (isShare || !first)) {
          setStage('failed');
          setError(st.error || 'Conversion failed');
          return;
        }
        if (isShare) {
          // Share visitors can't start conversions. While a render job is under
          // way (`active` — e.g. the owner just uploaded), wait for it; a file
          // that was never rendered needs the owner to open it once.
          if (st.active) {
            setStage('processing');
            timer = window.setTimeout(() => poll(false), 5000);
          } else {
            setStage('notprepared');
          }
          return;
        }
        if (first) {
          // Not rendered yet (or a previous attempt failed) — kick off a render.
          const t = await fetch(`/api/dwg2d/translate/${fileId}`, { method: 'POST', headers: authHeader() });
          if (!t.ok) {
            const body = await t.json() as { error?: string };
            throw new Error(body.error || 'Could not start conversion');
          }
          setStage('processing');
        }
        timer = window.setTimeout(() => poll(false), 5000);
      } catch (err) {
        if (!cancelled) {
          setStage('failed');
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };

    if (fileId) poll(true);
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [fileId, isShare, shareToken, authHeader, assetFetch, retryTick]);

  const totalMap = useCallback(() => {
    const wrap = wrapRef.current;
    const v = viewRef.current;
    if (!wrap) return { tx: 0, ty: 0, k: 1 };
    const rect = wrap.getBoundingClientRect();
    const vb = vbRef.current;
    if (!vb) return { tx: v.x, ty: v.y, k: v.scale };
    const s0 = Math.min(rect.width / vb.w, rect.height / vb.h);
    const offX = (rect.width - vb.w * s0) / 2 - vb.x * s0;
    const offY = (rect.height - vb.h * s0) / 2 - vb.y * s0;
    return { tx: v.x + v.scale * offX, ty: v.y + v.scale * offY, k: v.scale * s0 };
  }, []);

  const applyView = useCallback(() => {
    const v = viewRef.current;
    const m = totalMap();
    if (svgHostRef.current) {
      svgHostRef.current.style.transform = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
      // Cosmetic strokes: CAD draws every line at a constant SCREEN width at any
      // zoom; our SVG bakes world-width strokes that the scale() transform above
      // widens as you zoom in (the "lines get fat when zoomed" complaint). The
      // injected style below ties every stroke-width to --dwg-sw; keep it at a
      // fixed on-screen width by dividing out the total scale (m.k = zoom × fit).
      svgHostRef.current.style.setProperty('--dwg-sw', String(COSMETIC_STROKE_PX / Math.max(m.k, 1e-6)));
    }
    controllerRef.current?.setView(m);
    updateGeoDotRef.current();
  }, [totalMap]);

  updateGeoDotRef.current = () => {
    const dot = geoDotRef.current;
    if (!dot) return;
    const pose = lastPoseRef.current;
    const box = modelBboxRef.current;
    const vb = vbRef.current;
    // Legacy single-SVG conversions ARE model space (key null) — the bbox
    // attribute gate below excludes pre-georef renders anyway.
    const isModelTab = !tabsRef.current || activeTabKeyRef.current === 'model';
    const setOut = (out: boolean) => {
      if (geoOutRef.current !== out) { geoOutRef.current = out; setGeoOut(out); }
    };
    const setWaiting = (w: boolean) => {
      if (geoWaitingRef.current !== w) { geoWaitingRef.current = w; setGeoWaiting(w); }
    };
    if (!geoTrackOnRef.current || !box || !vb || !isModelTab) {
      dot.style.display = 'none';
      setOut(false);
      setWaiting(false);
      return;
    }
    if (!pose) {
      dot.style.display = 'none';
      setOut(false);
      setWaiting(true);
      return;
    }
    setWaiting(false);
    const f = metersToDrawingRef.current;
    const wx = pose.x * f;
    const wy = pose.y * f;
    const sx = vb.x + ((wx - box.minX) / (box.maxX - box.minX)) * vb.w;
    const sy = vb.y + ((box.maxY - wy) / (box.maxY - box.minY)) * vb.h;
    // Beyond ~25% of the drawing span the camera is simply not on this sheet —
    // hide the dot and surface the georeferencing hint instead.
    const margin = 0.25 * Math.max(vb.w, vb.h);
    const out = sx < vb.x - margin || sx > vb.x + vb.w + margin || sy < vb.y - margin || sy > vb.y + vb.h + margin;
    setOut(out);
    if (out) {
      dot.style.display = 'none';
      return;
    }
    const m = totalMap();
    dot.style.display = 'block';
    dot.style.transform = `translate(${sx * m.k + m.tx}px, ${sy * m.k + m.ty}px)`;
    // World heading θ (CCW from +X) → screen angle −θ (SVG y is flipped).
    if (geoWedgeRef.current) geoWedgeRef.current.style.transform = `rotate(${-pose.heading}rad)`;
  };

  useEffect(() => {
    geoTrackOnRef.current = geoTrackOn;
    updateGeoDotRef.current();
  }, [geoTrackOn]);

  useEffect(() => {
    if (!poseSource) return;
    lastPoseRef.current = poseSource.getLatest();
    updateGeoDotRef.current();
    return poseSource.subscribe((p) => {
      lastPoseRef.current = p;
      if (geoTrackOnRef.current) updateGeoDotRef.current();
    });
  }, [poseSource]);

  const applyFormatters = useCallback(() => {
    const k = scaleRef.current.unitsPerSvg;
    const u = unitRef.current;
    controllerRef.current?.setFormatters({
      len: (svgLen: number) => formatLength(svgLen * k, u),
      area: (svgArea: number) => formatArea(svgArea * k * k, u),
    });
  }, []);

  const persistDraft = useCallback((kind: MeasureKind, pts: Pt[]) => {
    const { distance, area } = measureValues(kind, pts, scaleRef.current.unitsPerSvg);
    const layout = activeTabKeyRef.current;
    const count = measurementsRef.current.filter((m) => m.kind === kind && m.layout === layout).length;
    const name = `${KIND_LABEL[kind]} ${count + 1}`;
    const tempId = `tmp-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const vm: MeasureVM = { id: tempId, kind, name, layout, pts, distance, area };
    setMeasurements((prev) => [...prev, vm]);
    setDrafting(false);

    if (isShare) return;
    create2dMeasurement({
      variables: {
        fileId,
        kind,
        points: pts.map((p) => ({ x: p.x, y: p.y, z: 0 })),
        distance,
        area,
        name,
        layout,
      },
    }).then((res) => {
      const saved = res.data?.create2dMeasurement;
      if (!saved) throw new Error('empty response');
      if (deletedTmpRef.current.has(tempId)) {
        // Deleted while the create was in flight — the row exists server-side
        // now; remove it there too.
        deletedTmpRef.current.delete(tempId);
        deleteMeasurement({ variables: { measurementId: saved.id } }).catch(() => undefined);
        return;
      }
      // A rename committed while the row still had its tmp id could not reach
      // the server — reconcile it now.
      const current = measurementsRef.current.find((m) => m.id === tempId);
      setMeasurements((prev) => prev.map((m) => (m.id === tempId ? { ...m, id: saved.id } : m)));
      setEditingId((cur) => (cur === tempId ? saved.id : cur));
      if (current && current.name !== name) {
        renameMeasurement({ variables: { measurementId: saved.id, name: current.name } }).catch(() => undefined);
      }
    }).catch(() => {
      setMeasurements((prev) => prev.map((m) => (m.id === tempId ? { ...m, unsaved: true } : m)));
    });
  }, [create2dMeasurement, deleteMeasurement, renameMeasurement, fileId, isShare]);

  const persistDraftRef = useRef(persistDraft);
  persistDraftRef.current = persistDraft;

  // ── Inject the SVG, detect its real-world scale, build the snap index ──────
  useEffect(() => {
    if (!svg || !svgHostRef.current) return;
    const host = svgHostRef.current;
    // Drawings converted before the Smart View theme match carry #101418 as
    // their baked background — normalise so old renders don't show a two-tone
    // seam against the #06080b page (new conversions bake #06080b directly).
    host.innerHTML = svg.text.replace(/#101418/gi, '#06080b');
    const el = host.querySelector('svg');
    let cancelled = false;
    if (el) {
      // The original width attribute ("100mm") is the only trace of the real
      // drawing size — capture it before it is overwritten for layout.
      const originalWidth = el.getAttribute('width');
      vbRef.current = parseViewBox(el);
      scaleRef.current = detectSvgScale(el, originalWidth);
      setLightBaked(el.getAttribute('data-bg-mode') === 'light');
      // Georeference: the exact model-space box the converter fit onto the
      // viewBox (only present on model tabs rendered after georef support).
      const mb = (el.getAttribute('data-model-bbox') || '').trim().split(/\s+/).map(Number);
      modelBboxRef.current =
        mb.length === 4 && mb.every(Number.isFinite) && mb[2] > mb[0] && mb[3] > mb[1]
          ? { minX: mb[0], minY: mb[1], maxX: mb[2], maxY: mb[3] }
          : null;
      idBboxRef.current = modelBboxRef.current;
      const metersPerUnit = INSUNITS_METERS[el.getAttribute('data-insunits') ?? ''];
      metersToDrawingRef.current = metersPerUnit ? 1 / metersPerUnit : 1;
      if (!metersPerUnit) modelBboxRef.current = null;   // no trusted unit → no dot
      setGeoStatus(modelBboxRef.current ? 'ok' : metersPerUnit ? 'no-bbox' : 'no-units');
      const unitKey = `geodraft_dwg2d_unit_${fileId}:${pendingTabKeyRef.current ?? 'main'}`;
      // Fall back to the pre-tabs key so previously saved unit choices survive.
      const savedUnit = (localStorage.getItem(unitKey)
        || localStorage.getItem(`geodraft_dwg2d_unit_${fileId}`)) as Unit | null;
      setUnit(savedUnit && UNITS.includes(savedUnit) ? savedUnit : scaleRef.current.defaultUnit);

      el.setAttribute('width', '100%');
      el.setAttribute('height', '100%');
      el.style.display = 'block';

      // Bind every stroke to --dwg-sw so applyView can hold a constant on-screen
      // line width across zoom levels (cosmetic strokes, like CAD). Uniform by
      // design — survey renders are near-single-weight hairlines anyway, and a
      // constant thin width is what keeps dense wall-hatch legible when zoomed in
      // instead of merging into solid bands. Fills (solid HATCH, text) are
      // unaffected: forcing stroke-width does nothing without a stroke.
      const cosmetic = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      cosmetic.textContent =
        'path,polyline,line,circle,ellipse,rect,polygon{stroke-width:var(--dwg-sw,1)!important}';
      el.appendChild(cosmetic);

      snapIndexRef.current = null;
      setSnapProgress(0);
      setSnapTruncated(false);
      const builder = createSnapIndexBuilder(el);
      const stepLoop = () => {
        if (cancelled) return;
        const done = builder.step(12);
        setSnapProgress(builder.progress());
        if (done) {
          snapIndexRef.current = builder.index;
          setSnapTruncated(builder.index.truncated);
        } else {
          window.setTimeout(stepLoop, 0);
        }
      };
      window.setTimeout(stepLoop, 0);

      if (overlayRef.current) {
        controllerRef.current?.destroy();
        const ctrl = new DWGMeasureOverlay(overlayRef.current);
        ctrl.onDraftComplete = (kind, pts) => persistDraftRef.current(kind, pts);
        controllerRef.current = ctrl;
        ctrl.setTheme(lightBgRef.current);
        applyFormatters();
        ctrl.setMeasurements(visibleOf(measurementsRef.current));
        ctrl.startTool(isMeasureKind(toolRef.current) ? toolRef.current : null);
      }
    }
    setIdPins([]);
    setIdOutside(false);
    viewRef.current = { x: 0, y: 0, scale: 1 };
    applyView();
    return () => { cancelled = true; };
  }, [svg, fileId, applyView, applyFormatters]);

  useEffect(() => {
    controllerRef.current?.setMeasurements(visibleOf(measurements));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurements, tabs, activeTab]);

  useEffect(() => {
    applyFormatters();
    if (stage === 'ready' && fileId && scaleRef.current) {
      localStorage.setItem(`geodraft_dwg2d_unit_${fileId}:${activeTabKeyRef.current ?? 'main'}`, unit);
    }
  }, [unit, stage, fileId, applyFormatters]);

  useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.startTool(isMeasureKind(tool) ? tool : null);
    ctrl.setSnapMarker(null);
    setDrafting(false);
    setIdOutside(false);
  }, [tool]);

  useEffect(() => {
    controllerRef.current?.setIdPins(idPins.map((p) => ({ doc: p.doc, label: idPointText(p.world) })));
  }, [idPins]);

  const ensureIdPoints = useCallback(async (i: number) => {
    const entry = tabs?.[i];
    if (!entry?.points) {
      setIdState('none');
      return;
    }
    if (idIndexRef.current.has(i)) {
      setIdState(idIndexRef.current.get(i) ? 'ready' : 'none');
      return;
    }
    if (idLoadingRef.current.has(i)) return;
    idLoadingRef.current.add(i);
    setIdState('loading');
    try {
      const qs = isShare ? `?shareToken=${encodeURIComponent(shareToken!)}&g=${tabGenRef.current}` : `?g=${tabGenRef.current}`;
      const res = await assetFetch(`tab-${i}.points.json.gz`, `/api/dwg2d/points/${fileId}/${i}${qs}`);
      const index = res.ok ? buildIdIndex(await jsonOf<IdPointsFile>(res)) : null;
      idIndexRef.current.set(i, index);
    } catch {
      idIndexRef.current.set(i, null);
    } finally {
      idLoadingRef.current.delete(i);
    }
    if (activeTabRef.current === i) setIdState(idIndexRef.current.get(i) ? 'ready' : 'none');
  }, [tabs, fileId, isShare, shareToken, authHeader, assetFetch]);

  useEffect(() => {
    if (stage === 'ready' && tool === 'id') ensureIdPoints(activeTab);
  }, [stage, tool, activeTab, ensureIdPoints]);

  addIdPinRef.current = (doc, hit) => {
    let world: IdWorld | null = hit ? hit.world : null;
    if (!world) {
      const index = idIndexRef.current.get(activeTabRef.current);
      let xy = index ? worldAt(index.frames, doc) : null;
      if (!xy && !index?.frames.length) {
        const isModelTab = !tabsRef.current || activeTabKeyRef.current === 'model';
        const frame = isModelTab && idBboxRef.current && vbRef.current ? bboxFrame(idBboxRef.current, vbRef.current) : null;
        xy = frame ? worldAt([frame], doc) : null;
      }
      world = xy ? { ...xy, z: null } : null;
    }
    if (!world) {
      setIdOutside(true);
      return;
    }
    setIdOutside(false);
    const pin: IdPin = { id: ++idSeqRef.current, doc, world, kind: hit ? hit.kind : 'cursor' };
    setIdPins((prev) => [...prev, pin].slice(-ID_PIN_LIMIT));
  };

  useEffect(() => {
    if (!snapOn) controllerRef.current?.setSnapMarker(null);
  }, [snapOn]);

  useEffect(() => {
    controllerRef.current?.setTheme(lightBg);
  }, [lightBg]);

  // ── Pointer: pan (drag) + zoom (wheel) + measuring (click / hover) ─────────
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || stage !== 'ready') return;

    let dragging = false;
    let moved = false;
    let lastX = 0, lastY = 0;
    let downX = 0, downY = 0;

    const mousePos = (e: MouseEvent) => {
      const rect = wrap.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const effectivePoint = (
      screen: { x: number; y: number }, shift: boolean,
    ): { doc: Pt; hit: ReturnType<typeof snapAt>; idHit: IdHit | null } => {
      const ctrl = controllerRef.current!;
      const map = totalMap();
      let doc = ctrl.toDoc(screen);
      let hit = null as ReturnType<typeof snapAt>;
      if (toolRef.current === 'id') {
        const index = idIndexRef.current.get(activeTabRef.current);
        const idHit = index ? nearestIdPoint(index, doc, SNAP_TOL_PX / map.k) : null;
        if (idHit) return { doc: idHit.doc, hit: { ...idHit.doc, type: 'survey' as const }, idHit };
      }
      const prev = ctrl.lastDraftPoint();
      // Ortho makes no sense for the rectangle tool — locking the second corner
      // to an axis collapses the rectangle to zero area.
      if (shift && prev && toolRef.current !== 'rect') {
        doc = orthoConstrain(prev, doc);
      } else if (snapOnRef.current && snapIndexRef.current) {
        hit = snapAt(snapIndexRef.current, doc, SNAP_TOL_PX / map.k);
        if (hit) doc = { x: hit.x, y: hit.y };
      }
      return { doc, hit, idHit: null };
    };

    const refreshHover = (screen: { x: number; y: number }, shift: boolean) => {
      const ctrl = controllerRef.current;
      if (!ctrl || toolRef.current === 'pan') return;
      const { doc, hit } = effectivePoint(screen, shift);
      ctrl.setSnapMarker(hit);
      ctrl.updateCursor(doc);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = viewRef.current;
      const rect = wrap.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0015);
      const next = Math.min(400, Math.max(0.2, v.scale * factor));
      const k = next / v.scale;
      // keep the drawing point under the cursor fixed while zooming
      v.x = mx - (mx - v.x) * k;
      v.y = my - (my - v.y) * k;
      v.scale = next;
      applyView();
      lastMouseRef.current = { x: mx, y: my, shift: e.shiftKey };
      refreshHover({ x: mx, y: my }, e.shiftKey);
    };

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0 && e.button !== 1) return;
      dragging = true;
      moved = false;
      lastX = e.clientX; lastY = e.clientY;
      downX = e.clientX; downY = e.clientY;
    };

    const onMove = (e: MouseEvent) => {
      const pos = mousePos(e);
      lastMouseRef.current = { ...pos, shift: e.shiftKey };
      if (dragging) {
        if (!moved && Math.hypot(e.clientX - downX, e.clientY - downY) > 4) moved = true;
        if (moved) {
          viewRef.current.x += e.clientX - lastX;
          viewRef.current.y += e.clientY - lastY;
          lastX = e.clientX; lastY = e.clientY;
          applyView();
        }
        return;
      }
      refreshHover(pos, e.shiftKey);
    };

    const onUp = (e: MouseEvent) => {
      if (!dragging) return;
      const wasClick = !moved;
      dragging = false;
      moved = false;
      if (!wasClick || e.button !== 0 || toolRef.current === 'pan') return;
      const ctrl = controllerRef.current;
      if (!ctrl) return;
      const { doc, hit, idHit } = effectivePoint(mousePos(e), e.shiftKey);
      ctrl.setSnapMarker(hit);
      if (toolRef.current === 'id') {
        addIdPinRef.current(doc, idHit);
        return;
      }
      const r = ctrl.addDraftPoint(doc);
      if (r === 'continue') setDrafting(true);
      if (r === 'complete') setDrafting(false);
    };

    const onDblClick = () => {
      const ctrl = controllerRef.current;
      if (!ctrl || toolRef.current === 'pan' || toolRef.current === 'id') return;
      if (ctrl.finishDraft()) {
        setDrafting(false);
      } else if (ctrl.draftPointCount() === 1) {
        // The second click of a dblclick that COMPLETED a line/rect starts a
        // stray one-point draft — drop it.
        ctrl.cancelDraft();
        setDrafting(false);
      }
    };

    const onEnter = () => { hoveredRef.current = true; };

    const onLeave = () => {
      hoveredRef.current = false;
      controllerRef.current?.setSnapMarker(null);
      controllerRef.current?.updateCursor(null);
    };

    wrap.addEventListener('wheel', onWheel, { passive: false });
    wrap.addEventListener('mousedown', onDown);
    wrap.addEventListener('dblclick', onDblClick);
    wrap.addEventListener('mouseenter', onEnter);
    // mouseenter never fires if the pane mounts under a stationary cursor
    wrap.addEventListener('mousemove', onEnter);
    wrap.addEventListener('mouseleave', onLeave);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      wrap.removeEventListener('wheel', onWheel);
      wrap.removeEventListener('mousedown', onDown);
      wrap.removeEventListener('dblclick', onDblClick);
      wrap.removeEventListener('mouseenter', onEnter);
      wrap.removeEventListener('mousemove', onEnter);
      wrap.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [stage, applyView, totalMap]);

  useEffect(() => {
    if (stage !== 'ready') return;
    const onKey = (e: KeyboardEvent) => {
      if (embedded && !hoveredRef.current) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const ctrl = controllerRef.current;
      if (e.key === 'Escape') {
        if (ctrl?.hasDraft()) {
          ctrl.cancelDraft();
          setDrafting(false);
        } else {
          setTool('pan');
        }
      } else if (e.key === 'Enter') {
        if (ctrl?.finishDraft()) setDrafting(false);
      } else if (e.key === 's' || e.key === 'S') {
        setSnapOn((v) => !v);
      } else if (e.key === 'l' || e.key === 'L') setTool('line');
      else if (e.key === 'p' || e.key === 'P') setTool('polyline');
      else if (e.key === 'r' || e.key === 'R') setTool('rect');
      else if (e.key === 'a' || e.key === 'A') setTool('area');
      else if (e.key === 'i' || e.key === 'I') setTool('id');
      else if (e.key === 'Shift' && lastMouseRef.current && ctrl && toolRef.current !== 'pan') {
        const prev = ctrl.lastDraftPoint();
        if (prev) {
          const doc = orthoConstrain(prev, ctrl.toDoc(lastMouseRef.current));
          ctrl.setSnapMarker(null);
          ctrl.updateCursor(doc);
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (embedded && !hoveredRef.current) return;
      if (e.key !== 'Shift' || !lastMouseRef.current) return;
      const ctrl = controllerRef.current;
      if (!ctrl || toolRef.current === 'pan' || !ctrl.hasDraft()) return;
      let doc = ctrl.toDoc(lastMouseRef.current);
      const map = totalMap();
      let hit = null as ReturnType<typeof snapAt>;
      if (snapOnRef.current && snapIndexRef.current) {
        hit = snapAt(snapIndexRef.current, doc, SNAP_TOL_PX / map.k);
        if (hit) doc = { x: hit.x, y: hit.y };
      }
      ctrl.setSnapMarker(hit);
      ctrl.updateCursor(doc);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [stage, totalMap, embedded]);

  useEffect(() => {
    if (stage !== 'ready' || !wrapRef.current) return;
    const ro = new ResizeObserver(() => applyView());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [stage, applyView]);

  const resetView = () => { viewRef.current = { x: 0, y: 0, scale: 1 }; applyView(); };

  const zoomToMeasurement = (id: string) => {
    const ctrl = controllerRef.current;
    const wrap = wrapRef.current;
    const vb = vbRef.current;
    if (!ctrl || !wrap || !vb) return;
    const box = ctrl.measurementBBox(id);
    if (!box) return;
    const rect = wrap.getBoundingClientRect();
    const s0 = Math.min(rect.width / vb.w, rect.height / vb.h);
    const minSpan = Math.max(vb.w, vb.h) * 0.002;
    const bw = Math.max(box.maxX - box.minX, minSpan) * 1.5;
    const bh = Math.max(box.maxY - box.minY, minSpan) * 1.5;
    const kTarget = Math.min(rect.width / bw, rect.height / bh);
    const v = viewRef.current;
    v.scale = Math.min(400, Math.max(0.2, kTarget / s0));
    const offX = (rect.width - vb.w * s0) / 2 - vb.x * s0;
    const offY = (rect.height - vb.h * s0) / 2 - vb.y * s0;
    const cx = (box.minX + box.maxX) / 2;
    const cy = (box.minY + box.maxY) / 2;
    v.x = rect.width / 2 - v.scale * (offX + s0 * cx);
    v.y = rect.height / 2 - v.scale * (offY + s0 * cy);
    applyView();
  };

  const switchTab = async (i: number) => {
    if (!tabs || i === activeTab || stage !== 'ready') return;
    controllerRef.current?.cancelDraft();
    setDrafting(false);
    const req = ++tabReqRef.current;
    try {
      let text = tabCacheRef.current.get(i);
      if (!text) {
        const shareQS = isShare ? `?shareToken=${encodeURIComponent(shareToken!)}&g=${tabGenRef.current}` : `?g=${tabGenRef.current}`;
        const res = await assetFetch(`tab-${i}.svg.gz`, `/api/dwg2d/tab/${fileId}/${i}${shareQS}`);
        if (!res.ok) throw new Error('tab fetch failed');
        text = await textOf(res);
        // Latest-wins: a newer switch or a reload superseded this fetch.
        if (req !== tabReqRef.current) return;
        tabCacheRef.current.set(i, text);
        if (tabCacheRef.current.size > 6) {
          const oldest = tabCacheRef.current.keys().next().value;
          if (oldest !== undefined && oldest !== i) tabCacheRef.current.delete(oldest);
        }
      }
      if (req !== tabReqRef.current) return;
      pendingTabKeyRef.current = tabs[i].key;
      setActiveTab(i);
      setSvg({ text });
    } catch { /* stay on the current tab */ }
  };

  const removeMeasurement = (m: MeasureVM) => {
    setMeasurements((prev) => prev.filter((x) => x.id !== m.id));
    controllerRef.current?.setHighlight(null);
    if (isShare) return;
    if (m.id.startsWith('tmp-')) {
      // The create may still be in flight — its .then deletes the saved row.
      deletedTmpRef.current.add(m.id);
      return;
    }
    deletedIdsRef.current.add(m.id);
    if (!m.unsaved) {
      deleteMeasurement({
        variables: { measurementId: m.id },
        // Evict the entity so cached fileMeasurements re-emissions cannot
        // resurrect the deleted row.
        update: (cache) => {
          cache.evict({ id: cache.identify({ __typename: 'Measurement', id: m.id }) });
          cache.gc();
        },
      }).catch(() => undefined);
    }
  };

  const startRename = (m: MeasureVM) => {
    setEditingId(m.id);
    setEditName(m.name);
  };

  const commitRename = () => {
    const id = editingId;
    if (!id) return;
    const name = editName.trim();
    setEditingId(null);
    if (!name) return;
    // Decide BEFORE dispatching: React defers the setMeasurements updater, so a
    // flag set inside it is not readable on the next line.
    const target = measurementsRef.current.find((m) => m.id === id);
    if (!target || target.name === name) return;
    setMeasurements((prev) => prev.map((m) => (m.id === id ? { ...m, name } : m)));
    if (!isShare && !id.startsWith('tmp-')) {
      renameMeasurement({ variables: { measurementId: id, name } }).catch(() => undefined);
    }
  };

  const valueText = (m: MeasureVM): string => {
    const u = unit;
    if (m.kind === 'rect' && m.pts.length >= 2) {
      const k = scaleRef.current.unitsPerSvg;
      const w = Math.abs(m.pts[1].x - m.pts[0].x) * k;
      const h = Math.abs(m.pts[1].y - m.pts[0].y) * k;
      return `${formatLength(w, u)} × ${formatLength(h, u)}  ·  ${m.area != null ? formatArea(m.area, u) : ''}`;
    }
    if (m.kind === 'area') {
      const a = m.area != null ? formatArea(m.area, u) : '—';
      const p = m.distance != null ? `  ·  P ${formatLength(m.distance, u)}` : '';
      return `${a}${p}`;
    }
    const d = m.distance != null ? formatLength(m.distance, u) : '—';
    return m.kind === 'polyline' ? `${d}  ·  ${m.pts.length} pts` : d;
  };

  const snapReady = snapProgress >= 1;

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      {!embedded && (
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_back</span>
              Back
            </button>
            <div className={styles.divider} />
            <div className={styles.fileInfo}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#0061ff' }}>architecture</span>
              <span className={styles.fileName}>{fileInfo?.originalName ?? 'Loading…'}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            {stage === 'ready' && (
              <>
                <button
                  className={styles.actionButton}
                  onClick={() => setLightBg((v) => !v)}
                  title={lightBg ? 'Switch to black background' : 'Switch to white background'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                    {lightBg ? 'dark_mode' : 'light_mode'}
                  </span>
                  {lightBg ? 'Black' : 'White'}
                </button>
                <button className={styles.actionButton} onClick={resetView}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>fit_screen</span>
                  Fit
                </button>
              </>
            )}
          </div>
        </header>
      )}

      <div className={styles.viewerBody}>
        <div
          ref={wrapRef}
          style={{
            position: 'absolute', inset: 0, overflow: 'hidden',
            cursor: tool === 'pan' ? 'grab' : 'crosshair',
            background: lightBg ? '#f9f7f4' : '#06080b',
          }}
        >
          <div
            ref={svgHostRef}
            style={{ width: '100%', height: '100%', transformOrigin: '0 0', filter: lightBg !== lightBaked ? 'invert(1) hue-rotate(180deg)' : 'none' }}
          />
        </div>

        <svg ref={overlayRef} className={styles.measureOverlay} />

        {embedded && poseSource && (
          <>
            <div ref={geoDotRef} className={styles.geoDot} style={{ display: 'none' }}>
              <div ref={geoWedgeRef} className={styles.geoDotWedge} />
              <div className={styles.geoDotCore} />
            </div>
            {geoTrackOn && geoOut && (
              <div className={styles.geoChip}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>location_off</span>
                Position outside this drawing — is it georeferenced to the point cloud's coordinate system?
              </div>
            )}
            {geoTrackOn && !geoOut && geoWaiting && (
              <div className={styles.geoChip}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>hourglass_empty</span>
                Waiting for a camera position from the point-cloud pane…
              </div>
            )}
          </>
        )}

        {stage === 'ready' && (
          <div className={styles.measureToolbar}>
            {TOOLS.map((t) => (
              <button
                key={t.tool}
                className={`${styles.toolButton} ${tool === t.tool ? styles.toolButtonActive : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setTool(t.tool)}
                title={`${t.label} (${t.key})`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 19 }}>{t.icon}</span>
              </button>
            ))}
            <div className={styles.toolbarDivider} />
            <button
              className={`${styles.toolButton} ${snapOn ? styles.toolButtonActive : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSnapOn((v) => !v)}
              title={`Snap to drawing geometry (S) — ${snapOn ? 'on' : 'off'}`}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 19 }}>my_location</span>
            </button>
            {embedded && (
              <>
                <div className={styles.toolbarDivider} />
                <button
                  className={styles.toolButton}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setLightBg((v) => !v)}
                  title={lightBg ? 'Switch to black background' : 'Switch to white background'}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 19 }}>
                    {lightBg ? 'dark_mode' : 'light_mode'}
                  </span>
                </button>
                <button
                  className={styles.toolButton}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={resetView}
                  title="Fit to screen"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 19 }}>fit_screen</span>
                </button>
                {poseSource && (
                  <button
                    className={`${styles.toolButton} ${geoTrackOn ? styles.toolButtonActive : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setGeoTrackOn((v) => !v)}
                    disabled={geoStatus !== 'ok'}
                    title={geoStatus === 'ok'
                      ? (geoTrackOn ? 'Hide live position from the point cloud' : 'Show live position from the point cloud (uses the drawing’s georeferencing)')
                      : geoStatus === 'no-units'
                        ? 'Live position unavailable — the drawing declares no usable unit ($INSUNITS), so world metres can’t be mapped onto it'
                        : 'Live position needs the model-space tab of a drawing converted with georeference support — re-convert this file to enable it'}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 19 }}>radar</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {stage === 'ready' && tabs && tabs.length > 1 && (
          <div className={styles.tabBar}>
            {tabs.map((t, i) => (
              <button
                key={t.key}
                className={`${styles.tab} ${i === activeTab ? styles.tabActive : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => switchTab(i)}
                title={t.name}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}

        {stage === 'ready' && tool !== 'pan' && (
          <div className={`${styles.hintBar} ${tabs && tabs.length > 1 ? styles.hintBarRaised : ''}`}>
            {!snapReady && <span className={styles.hintProgress}>Indexing geometry… {Math.round(snapProgress * 100)}%</span>}
            {snapReady && snapTruncated && <span className={styles.hintProgress}>Snap limited (very large drawing)</span>}
            {tool === 'id'
              ? ID_HINTS[idOutside ? (idIndexRef.current.get(activeTab) ? 'outside' : 'legacySheet') : idState]
              : HINTS[tool as MeasureKind]}
            {drafting ? ' — in progress' : ''}
          </div>
        )}

        {stage === 'ready' && (
          <div className={styles.measurePanel}>
            <div className={styles.panelHeader}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#0061ff' }}>square_foot</span>
              <span className={styles.panelTitle}>Measurements</span>
              <span className={styles.panelCount}>{visibleMeasurements.length}</span>
              <select
                className={styles.unitSelect}
                value={unit}
                onChange={(e) => { setUnit(e.target.value as Unit); e.target.blur(); }}
                title="Drawing unit"
              >
                {UNITS.map((u) => <option key={u} value={u}>{u === 'u' ? 'unit' : u}</option>)}
              </select>
              <button className={styles.panelToggle} onClick={() => setPanelOpen((v) => !v)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                  {panelOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>
            </div>
            {panelOpen && (
              <div className={styles.panelBody}>
                {idPins.length > 0 && (
                  <div className={styles.idSection}>
                    <div className={styles.idHeader}>
                      <span className={styles.idTitle}>Identified points</span>
                      <button className={styles.idClear} title="Clear identified points" onClick={() => setIdPins([])}>
                        Clear
                      </button>
                    </div>
                    {idPins.slice().reverse().map((p, i) => (
                      <div key={p.id} className={styles.measureRow}>
                        <span className={`material-symbols-outlined ${styles.rowIcon}`}>pin_drop</span>
                        <div className={styles.rowMain}>
                          <div className={styles.rowName}>
                            <span className={styles.rowNameText}>Point {idPins.length - i}</span>
                            <span className={styles.idKind}>{ID_KIND_LABEL[p.kind]}</span>
                          </div>
                          <div className={styles.idCoords}>
                            <span>X {formatIdCoord(p.world.x)}</span>
                            <span>Y {formatIdCoord(p.world.y)}</span>
                            <span className={p.world.z === null ? styles.idMissing : undefined}>Z {formatIdCoord(p.world.z)}</span>
                          </div>
                        </div>
                        <button
                          className={styles.rowButton}
                          title="Copy X, Y, Z"
                          onClick={() => { navigator.clipboard?.writeText(idPointClipboard(p.world)).catch(() => {}); }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>content_copy</span>
                        </button>
                        <button
                          className={`${styles.rowButton} ${styles.rowButtonDanger}`}
                          title="Remove"
                          onClick={() => setIdPins((prev) => prev.filter((x) => x.id !== p.id))}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {visibleMeasurements.length === 0 && idPins.length === 0 && (
                  <div className={styles.panelEmpty}>
                    No measurements{tabs ? ' on this tab' : ''} yet.<br />Pick a tool on the left and click in the drawing.
                  </div>
                )}
                {visibleMeasurements.map((m) => (
                  <div
                    key={m.id}
                    className={styles.measureRow}
                    onMouseEnter={() => controllerRef.current?.setHighlight(m.id)}
                    onMouseLeave={() => controllerRef.current?.setHighlight(null)}
                    onClick={() => zoomToMeasurement(m.id)}
                  >
                    <span className={`material-symbols-outlined ${styles.rowIcon}`}>{KIND_ICON[m.kind]}</span>
                    <div className={styles.rowMain}>
                      {editingId === m.id ? (
                        <input
                          className={styles.rowNameInput}
                          value={editName}
                          autoFocus
                          onChange={(e) => setEditName(e.target.value)}
                          onBlur={commitRename}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div
                          className={styles.rowName}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => { e.stopPropagation(); if (!m.readOnly) startRename(m); }}
                        >
                          <span className={styles.rowNameText}>{m.name || KIND_LABEL[m.kind]}</span>
                          {m.unsaved && <span className={styles.unsavedBadge} title="Could not be saved">not saved</span>}
                        </div>
                      )}
                      <div className={styles.rowValue}>{valueText(m)}</div>
                    </div>
                    {!m.readOnly && (
                      <>
                        <button
                          className={styles.rowButton}
                          title="Rename"
                          onClick={(e) => { e.stopPropagation(); startRename(m); }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                        </button>
                        <button
                          className={`${styles.rowButton} ${styles.rowButtonDanger}`}
                          title="Delete"
                          onClick={(e) => { e.stopPropagation(); removeMeasurement(m); }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {isShare && visibleMeasurements.some((m) => !m.readOnly) && (
                  <div className={styles.panelNote}>Measurements you add on a share link are not saved.</div>
                )}
              </div>
            )}
          </div>
        )}

        {(stage === 'checking' || stage === 'processing') && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(4,120,87,0.7)' }}>sync</span>
            </div>
            <p className={styles.overlayTitle}>{stage === 'checking' ? 'Checking…' : 'Rendering Drawing…'}</p>
            <p className={styles.overlayText}>
              {stage === 'processing'
                ? 'The drawing is being rendered on our own conversion service. The first open takes a few minutes; afterwards it loads instantly.'
                : 'Checking whether this drawing is already rendered.'}
            </p>
            <div className={styles.progressBar}><div className={styles.progressFillIndeterminate} /></div>
          </div>
        )}

        {stage === 'loading' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(0,97,255,0.7)' }}>downloading</span>
            </div>
            <p className={styles.overlayTitle}>Loading Drawing…</p>
            <div className={styles.progressBar}><div className={styles.progressFillIndeterminate} /></div>
          </div>
        )}

        {stage === 'notprepared' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(4,120,87,0.7)' }}>architecture</span>
            </div>
            <p className={styles.overlayTitle}>Not Prepared for Viewing</p>
            <p className={styles.overlayText}>
              This drawing has not been rendered for online viewing yet. Ask the project owner to open it once, or use the download option on the share page.
            </p>
          </div>
        )}

        {stage === 'failed' && (
          <div className={styles.overlay}>
            <div className={styles.overlayIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: 'rgba(220,38,38,0.7)' }}>error</span>
            </div>
            <p className={styles.overlayTitle}>Failed</p>
            <div className={styles.errorBox}>{error}</div>
            {!isShare && (
              <button
                className={styles.actionButton}
                style={{ marginTop: 12 }}
                onClick={() => { setError(''); setStage('checking'); setRetryTick((t) => t + 1); }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span>
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DWGViewer;
